// SKU generation for inventory items.
//
// Session 78s.3, built to docs/gates/Stede_SkuGeneration_Gate_471.html
// (issue #471, approved by Colby at Session 78s.2).
//
// WHAT THIS IS FOR, in plain terms. D&R has short codes for the signs it
// sells and none for the materials it buys, because materials were tracked
// on paper by name. This turns "Stake DT 23x16-48" into "STK-DT-23X16-48",
// so a person can read a code off a shelf label and know what it belongs to.
//
// THE ONE RULE THAT GOVERNS EVERYTHING HERE, and it is gate decision S8:
// the code produced by this file is a DEFAULT, not a derived field. It is
// generated once, shown to the person creating the item, editable in the
// same keystroke, and then FROZEN. It is never recomputed - not when the
// item is renamed, and not when it is moved to another category. The
// category lives in `inventory_items.category_id`, where it is authoritative
// and where every screen already reads it.
//
// A future session proposing "regenerate on recategorization" is reopening a
// settled question, not raising a new one. The gate records why: regenerating
// invalidates every printed label in the building and every historical
// reference to the item, to fix a code that is only briefly wrong about a
// category nobody reads it for.
//
// THIS MODULE TOUCHES NO DATABASE, deliberately (gate decision, and it is
// what makes all 42 real catalog items testable with no fixture database).
// It takes a category code, a subcategory code and a name, and returns a
// string. Collision resolution is the CALLER's job, because only the caller
// knows which database it is writing to - see nextAvailableSku() below,
// which is shared so that the three creation paths cannot drift apart, but
// which still takes the existing codes from its caller rather than fetching
// them.
//
// The three creation paths that must all use this, or none:
//   1. InventoryModals.js       - the Add item form
//   2. promote_off_catalog_item() caller - promoting an off-catalog line
//   3. masterDataTemplates.js   - the CSV import, where blank means generate
// Missing one grows a catalog of codeless items, which is #465's class in a
// new costume.

// ---------------------------------------------------------------------------
// The shape: CAT-SUB-SIZE-VARIANT
// ---------------------------------------------------------------------------
//
// STK - DT - 23X16-48
// SHT - CTX - 6MM-4X8 - BLK
//  |     |      |        |
//  |     |      |        `- variant: colour, rider, finish
//  |     |      `---------- size: every token in the name carrying a digit,
//  |     |                  in the order the name gives them, NEVER normalised
//  |     `----------------- subcategory code, STORED on the category row
//  `----------------------- category code, STORED on the category row
//
// Segments that have no content are omitted rather than padded. "Stake H-Wire"
// is in a subcategory of one and carries no size at all, so its complete and
// valid SKU is STK-HW. The code ends early rather than inventing filler.

export const SKU_SEPARATOR = '-';

// Why the codes are stored on the category rather than derived from its name
// (gate decisions S3 and S4): "Stakes" and "Sheeted Materials" both begin with
// S, so a derived prefix collides; and renaming a category would silently
// change every code generated afterwards, which is the exact failure the whole
// freeze rule exists to prevent. The codes live in `inventory_categories.code`
// and are backfilled by
// db/migrations/20260904_sku_generation_category_codes.sql.

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

// A token belongs in the SIZE segment if it carries a digit. That is the whole
// discriminator, and it is deliberately dumb: "4x8", "6mm", "24", "16-48" and
// "23x16-48" all qualify, while "White", "Roll" and "Rider" do not. Anything
// cleverer would have to understand units, and would then be wrong about the
// first material D&R buys that this file has never seen.
const carriesDigit = (token) => /\d/.test(token);

// Gate decision S6: dimensions are NEVER normalised. "Frame Std 18x24" and
// "Frame Std 24x18" are two different items that D&R stocks separately, so the
// numbers are not sorted, not zero-padded, and the x is kept. That is also why
// the hand-typed specimen found on production, SDT231648, was rejected as a
// model - it is genuinely ambiguous between 23-16-48 and 231-648.
const sizeToken = (token) => token.toUpperCase();

// Words are matched with a trailing "s" removed from both sides, so the
// category "Frames" swallows the word "Frame" and the subcategory "Steel
// Faces" swallows "Steel" and "Face". This is forced by the real names: every
// item repeats its own category in its name, and joined naively "Std Frames"
// under "Frames" reads FRM-STDFRM (gate decision S4).
const stem = (word) => word.toLowerCase().replace(/s$/, '');

// Words whose short code is a CONVENTION rather than a derivation.
//
// This table exists because of one word, and it is worth stating plainly:
// BLACK abbreviates to BLK everywhere in the world, and no mechanical rule
// produces that. Dropping vowels and taking three gives BLC, which is both
// wrong and unreadable. The same rule gives the right answer for every other
// value in D&R's catalog - WHITE -> WHT, BRUSHED -> BRS - so BLACK is a genuine
// exception rather than evidence the rule is wrong.
//
// It is seeded with exactly the words D&R actually stocks today. Colours and
// finishes are NOT guessed ahead of time on purpose: inventing an entry for a
// value nobody has bought is inventing a product decision, and a wrong guess
// here reaches a printed label. When a new colour appears and its generated
// code reads badly, add it here deliberately.
const CONVENTIONAL_ABBREVIATIONS = {
  BLACK: 'BLK',
};

// Contract a single leftover word to a short code. Words of four characters or
// fewer are kept whole because shortening them costs readability and buys one
// character: "Roll" stays ROLL. Longer words drop their vowels and take three:
// White -> WHT, Brushed -> BRS. Both are real values in D&R's catalog and both
// appear in the gate's worked table.
const contractWord = (word) => {
  const clean = word.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (CONVENTIONAL_ABBREVIATIONS[clean]) return CONVENTIONAL_ABBREVIATIONS[clean];
  if (clean.length <= 4) return clean;
  const consonants = clean.replace(/[AEIOU]/g, '');
  return (consonants.length >= 3 ? consonants : clean).slice(0, 3);
};

// Several leftover words become their initials: "Top Rider" -> TR, "Bot Rider"
// -> BR. A two-word variant contracted the other way would read TOPRDR, which
// is longer than the size segment it qualifies.
const contractPhrase = (words) => {
  if (words.length === 0) return '';
  if (words.length === 1) return contractWord(words[0]);
  return words.map((w) => w.toUpperCase().replace(/[^A-Z0-9]/g, '').charAt(0)).join('');
};

const wordsOf = (value) => String(value || '').trim().split(/\s+/).filter(Boolean);

// ---------------------------------------------------------------------------
// The generator
// ---------------------------------------------------------------------------

/**
 * Build the default SKU for an item.
 *
 * Pure. Reads nothing, writes nothing, and does not know which database the
 * caller is pointed at.
 *
 * @param {object}  input
 * @param {string}  input.categoryCode     `inventory_categories.code` for the parent
 * @param {string}  input.subcategoryCode  `inventory_categories.code` for the child, if any
 * @param {string}  input.categoryName     parent name, used only to strip repeated words
 * @param {string}  input.subcategoryName  child name, used only to strip repeated words
 * @param {string}  input.name             the item name as typed
 * @returns {string} the SKU, or '' when there is not enough to build one
 */
export function generateSku({
  categoryCode = '',
  subcategoryCode = '',
  categoryName = '',
  subcategoryName = '',
  name = '',
} = {}) {
  const cat = String(categoryCode || '').trim().toUpperCase();
  const sub = String(subcategoryCode || '').trim().toUpperCase();

  // No category code means the category has not been given one yet. Returning
  // empty is correct and is not an error: the column is nullable on purpose,
  // and the Add-item form falls back to an empty box the person can type in
  // rather than refusing to save the item.
  if (!cat) return '';

  // Words already carried by the category and subcategory codes are removed
  // from the name, so "Frame Std 18x24" contributes only "18x24".
  const owned = new Set([...wordsOf(categoryName), ...wordsOf(subcategoryName)].map(stem));

  const remaining = wordsOf(name).filter((token) => !owned.has(stem(token)));

  const size = remaining.filter(carriesDigit).map(sizeToken);
  const variant = contractPhrase(remaining.filter((token) => !carriesDigit(token)));

  // Gate decision S17: an item with no subcategory omits the segment entirely
  // rather than padding it, so a top-level frame reads FRM-18X24.
  return [cat, sub, ...size, variant].filter(Boolean).join(SKU_SEPARATOR);
}

/**
 * Resolve a collision by appending a numeric suffix (gate decision S10).
 *
 * Shared so the three creation paths cannot invent three different suffix
 * schemes, but still caller-driven: `taken` is supplied by whoever is about to
 * write, because only they know which database they are writing to and what is
 * already in it. `inventory_categories` has no uniqueness constraint on name
 * (#468's M9), so two different parents can each hold a subcategory called
 * Cast - which is precisely how two unrelated items reach the same base code.
 *
 * @param {string} base   the SKU from generateSku()
 * @param {Iterable<string>} taken  SKUs already stored
 * @returns {string} `base` if free, else base-2, base-3, ...
 */
export function nextAvailableSku(base, taken = []) {
  if (!base) return '';
  const used = new Set([...taken].filter(Boolean).map((s) => String(s).trim().toUpperCase()));
  if (!used.has(base.toUpperCase())) return base;
  // Starts at 2 rather than 1: the unsuffixed code IS the first one, and
  // "FRM-STD-18X24-1" beside "FRM-STD-18X24" reads as if one of them is wrong.
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base}${SKU_SEPARATOR}${n}`;
    if (!used.has(candidate.toUpperCase())) return candidate;
  }
  return '';
}

/**
 * Build the default SKU from the app's flat category list and the chosen
 * category id.
 *
 * Still pure. This exists so the three creation paths cannot each invent their
 * own way of turning a `category_id` into a code pair - which is exactly how
 * they would drift apart, and drifting apart is the thing the gate warns about
 * by name.
 *
 * The picker in the Add-item form only offers CHILD categories, so in practice
 * `categoryId` is a subcategory. It is not guaranteed: production holds items
 * filed directly on a top-level category, and gate decision S17 covers them by
 * omitting the subcategory segment rather than by refusing.
 *
 * @param {object} input
 * @param {Array}  input.categories  the flat list, each {id, name, code, parent_id}
 * @param {number|string} input.categoryId  the chosen category
 * @param {string} input.name        the item name as typed
 * @returns {string} the SKU, or '' when the category is unknown or uncoded
 */
export function generateSkuForCategory({ categories = [], categoryId, name = '' } = {}) {
  const same = (a, b) => String(a) === String(b);
  const chosen = categories.find((c) => same(c.id, categoryId));
  if (!chosen) return '';

  const parent = chosen.parent_id
    ? categories.find((c) => same(c.id, chosen.parent_id))
    : null;

  // A child category: the parent supplies the first segment, the child the
  // second. Both names are passed so their words can be stripped out of the
  // item name - "Frame Std 18x24" must contribute only "18x24".
  if (parent) {
    return generateSku({
      categoryCode: parent.code,
      subcategoryCode: chosen.code,
      categoryName: parent.name,
      subcategoryName: chosen.name,
      name,
    });
  }

  // A top-level category: S17, the subcategory segment is omitted entirely.
  return generateSku({
    categoryCode: chosen.code,
    categoryName: chosen.name,
    name,
  });
}
